import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, SlidersHorizontal } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu.js';
import { Input } from './input.js';
import { Skeleton } from './misc.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';

/**
 * The table's own chrome. Passed in rather than translated here: primitives
 * under `components/ui` must stay usable without an i18n provider.
 */
export interface DataTableLabels {
  columns?: string;
  showColumns?: string;
  previous?: string;
  next?: string;
  page?: (page: number, total: number) => string;
}

const DEFAULT_LABELS: Required<DataTableLabels> = {
  columns: 'Columns',
  showColumns: 'Show columns',
  previous: 'Previous',
  next: 'Next',
  page: (page, total) => `Page ${page} of ${total}`,
};

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  /** Enables the search box and filters on this column id. */
  searchColumn?: string;
  searchPlaceholder?: string;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  /** Rendered beside the search box — filter selects, an action button. */
  toolbar?: ReactNode;
  pageSize?: number;
  /** Turn off client paging when the server already pages. */
  paginate?: boolean;
  labels?: DataTableLabels;
  className?: string;
}

/**
 * TanStack Table with the Zuruflex chrome: sorting, column visibility, search and
 * paging. Server-paged tables pass `paginate={false}` and drive the data
 * themselves.
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  searchColumn,
  searchPlaceholder = 'Search…',
  onRowClick,
  emptyMessage = 'Nothing here yet.',
  toolbar,
  pageSize = 20,
  paginate = true,
  labels,
  className,
}: DataTableProps<TData, TValue>) {
  const text = { ...DEFAULT_LABELS, ...labels };
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, globalFilter },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(paginate ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    initialState: { pagination: { pageSize } },
  });

  const hideableColumns = table.getAllColumns().filter((column) => column.getCanHide());

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {searchColumn || toolbar ? (
        <div className="flex flex-wrap items-center gap-3">
          {searchColumn ? (
            <Input
              value={globalFilter}
              onChange={(event) => setGlobalFilter(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 max-w-xs"
            />
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            {toolbar}
            {hideableColumns.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <SlidersHorizontal />
                    {text.columns}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{text.showColumns}</DropdownMenuLabel>
                  {hideableColumns.map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-card border border-border-default bg-surface-raised">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <TableHead key={header.id} style={{ width: header.getSize() === 150 ? undefined : header.getSize() }}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 transition-colors hover:text-text"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ChevronsUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index}>
                  {columns.map((_column, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-4 w-full rounded-md" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="py-14 text-center text-text-muted">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={onRowClick ? 'cursor-pointer hover:bg-surface-hover' : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {paginate && table.getPageCount() > 1 ? (
        <div className="flex items-center justify-between text-sm">
          <p className="text-text-muted">
            {text.page(table.getState().pagination.pageIndex + 1, table.getPageCount())}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft />
              {text.previous}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {text.next}
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export type { ColumnDef } from '@tanstack/react-table';
